import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X, Sparkles, ShieldCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import AuthDialog from "./AuthDialog";

const NAV = [
  { to: "/shop", label: "Shop All" },
  { to: "/shop/booster-boxes", label: "Booster Boxes" },
  { to: "/shop/elite-trainer-boxes", label: "ETBs" },
  { to: "/shop/single-cards", label: "Singles" },
  { to: "/shop/plushies", label: "Plushies" },
  { to: "/shop/sleeves", label: "Sleeves" },
  { to: "/shop/toploaders", label: "Toploaders" },
  { to: "/track", label: "Track Order" },
];

export default function Header() {
  const { itemCount, setOpen } = useCart();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) nav(`/shop?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <>
      {/* Top utility ribbon */}
      <div className="w-full bg-[#0A0812] border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase text-white/50 font-mono">
          <span>Free shipping on orders over £50</span>
          <span className="hidden md:flex items-center gap-2">
            <Sparkles size={12} className="text-[#B026FF]" />
            Earn 1 Kavo Point per £1 — redeem at checkout
          </span>
        </div>
      </div>

      <header className="glass sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center gap-6">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
            <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
              <span className="absolute inset-0 rounded-full border border-[#B026FF]/40" />
              <span className="absolute inset-1 rounded-full border border-[#B026FF]/20" />
              <span
                className="font-display font-extrabold text-2xl leading-none text-transparent"
                style={{ WebkitTextStroke: "1px rgba(176,38,255,0.85)" }}
              >
                K
              </span>
            </div>
            <div className="font-display font-extrabold tracking-tight text-xl text-white">
              KAVO<span className="text-[#B026FF]">.</span>CARDS
            </div>
          </Link>

          {/* Search */}
          <form onSubmit={onSearch} className="hidden md:flex flex-1 items-center max-w-xl">
            <div className="flex items-center w-full bg-[#0A0812] border border-white/10 rounded-full px-4 h-10 focus-within:border-[#B026FF]/60 transition">
              <Search size={16} className="text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="nav-search-input"
                placeholder="Search the vault…"
                className="bg-transparent outline-none text-sm text-white/90 placeholder:text-white/30 ml-3 w-full"
              />
            </div>
          </form>

          {/* Right utilities */}
          <div className="ml-auto flex items-center gap-2">
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin-link"
                className="hidden md:inline-flex items-center gap-2 px-3 h-10 rounded-full border border-[#B026FF]/40 bg-[#B026FF]/10 text-[#E1B7FF] hover:border-[#B026FF]/70 transition text-sm"
              >
                <ShieldCheck size={14} /> Admin
              </NavLink>
            )}
            {user ? (
              <NavLink
                to="/account"
                data-testid="nav-account-link"
                className="hidden md:flex items-center gap-2 px-3 h-10 rounded-full border border-white/10 hover:border-[#B026FF]/50 transition"
              >
                <User size={16} className="text-white/70" />
                <span className="text-sm text-white/90">{user.name?.split(" ")[0] || "Account"}</span>
                <span className="font-mono text-[11px] text-[#B026FF]">{user.loyalty_points} pts</span>
              </NavLink>
            ) : (
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                onClick={() => setAuthOpen(true)}
                className="hidden md:flex text-white/80 hover:text-white hover:bg-white/5"
              >
                <User size={16} className="mr-2" /> Sign in
              </Button>
            )}

            <button
              data-testid="nav-cart-btn"
              onClick={() => setOpen(true)}
              className="relative h-10 px-4 rounded-full btn-violet flex items-center gap-2 font-medium text-sm"
            >
              <ShoppingBag size={16} />
              <span>Cart</span>
              <span className="font-mono text-xs bg-black/30 px-2 py-0.5 rounded-full">{itemCount}</span>
            </button>

            <button
              className="md:hidden text-white/80 ml-1"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mega-nav row */}
        <nav className="hidden md:block border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 h-12 flex items-center gap-8 overflow-x-auto">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `text-[12px] tracking-[0.22em] uppercase font-medium whitespace-nowrap transition ${
                    isActive ? "text-[#B026FF]" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <span className="ml-auto text-[11px] tracking-[0.22em] uppercase text-white/40 font-mono">
              Authenticated • Sealed • Trusted
            </span>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/5 bg-[#0A0812]">
            <div className="px-6 py-4 flex flex-col gap-3">
              <form onSubmit={onSearch} className="flex">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  data-testid="mobile-search-input"
                  className="bg-[#110E1D] border border-white/10 rounded-md px-3 py-2 text-sm w-full text-white"
                />
              </form>
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm py-1 text-white/80"
                >
                  {n.label}
                </NavLink>
              ))}
              {user ? (
                <NavLink to="/account" onClick={() => setMobileOpen(false)} className="text-sm py-1 text-[#B026FF]">
                  Account ({user.loyalty_points} pts)
                </NavLink>
              ) : (
                <button
                  onClick={() => { setMobileOpen(false); setAuthOpen(true); }}
                  data-testid="mobile-login-btn"
                  className="text-sm py-1 text-[#B026FF] text-left"
                >
                  Sign in
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
